/**
 * Installment Payments Manager - Handles dynamic Klarna and Afterpay placement creation and updates
 * Reusable across product-hero, quick-view, and installment-payments components
 */

class InstallmentPaymentsManager {
  constructor() {
    this.containers = new Map();
    this.observers = new Map();
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.updateAllPlacements();
        this.updateAllAfterpayPlacements();
      });
    } else {
      this.updateAllPlacements();
      this.updateAllAfterpayPlacements();
    }

    setTimeout(() => this.updateAllPlacements(), 1000);
    setTimeout(() => this.updateAllPlacements(), 3000);

    setTimeout(() => this.updateAllAfterpayPlacements(), 2000);
    setTimeout(() => this.updateAllAfterpayPlacements(), 5000);

    this.setupMutationObservers();
    this.monitorAfterpayAvailability();
  }

  /**
   * Monitor when Afterpay script becomes available
   */
  monitorAfterpayAvailability() {
    const checkAfterpay = () => {
      if (typeof Afterpay !== 'undefined') {
        this.updateAllAfterpayPlacements();
      } else {
        setTimeout(checkAfterpay, 1000);
      }
    };
    checkAfterpay();
  }

  /**
   * Create a Klarna placement element with proper attributes
   */
  createKlarnaPlacement(config, price) {
    if (!price || price <= 0) return null;

    const klarnaElement = document.createElement('klarna-placement');
    klarnaElement.setAttribute('id', 'klarna-placement-auto-size');
    klarnaElement.setAttribute('data-key', 'credit-promotion-auto-size');
    klarnaElement.setAttribute('data-locale', config.locale);
    klarnaElement.setAttribute('data-purchase-amount', price);
    klarnaElement.setAttribute('data-purchase-country', config.country);
    klarnaElement.setAttribute('data-currency', config.currency);
    klarnaElement.setAttribute('data-theme', 'light');

    return klarnaElement;
  }

  /**
   * Update a specific Klarna container with new price
   */
  updateContainer(container, price) {
    if (!container) return;

    const config = JSON.parse(container.dataset.klarnaConfig || '{}');

    const existingKlarna = container.querySelector('klarna-placement');
    if (existingKlarna) {
      existingKlarna.remove();
    }

    if (price && price > 0) {
      const klarnaElement = this.createKlarnaPlacement(config, price);
      if (klarnaElement) {
        container.appendChild(klarnaElement);
        this.showOrText(container);
        this.reinitializeKlarna();
      }
    } else if (price === 0) {
      this.hideOrText(container);
    }
  }

  /**
   * Show "or" text when Klarna is available
   */
  showOrText(container) {
    const orText = container.parentElement?.querySelector('.klarna-or-text');
    if (orText) {
      orText.style.display = 'inline';
    }
  }

  /**
   * Hide "or" text when Klarna is not available
   */
  hideOrText(container) {
    const orText = container.parentElement?.querySelector('.klarna-or-text');
    if (orText) {
      orText.style.display = 'none';
    }
  }

  /**
   * Reinitialize Klarna SDK
   */
  reinitializeKlarna() {
    if (window.Klarna && window.Klarna.autoSize) {
      window.Klarna.autoSize();
    }
  }

  /**
   * Get price from various sources
   */
  getPrice(container) {
    let price = null;

    const installmentContainer = container.querySelector('klarna-placement');
    if (installmentContainer && installmentContainer.dataset.purchaseAmount) {
      price = parseFloat(installmentContainer.dataset.purchaseAmount);
    }

    if (!price) {
      const heroContainer = container.closest('.product-hero');
      if (heroContainer) {
        const priceElement = heroContainer.querySelector('.product-hero__price, .product-hero__discount-price');
        if (priceElement) {
          const priceText = priceElement.textContent.replace(/[^0-9.]/g, '');
          price = parseFloat(priceText) * 100;
        }
      }

      const quickViewContainer = container.closest('.quick-view');
      if (quickViewContainer) {
        const priceElement = quickViewContainer.querySelector('.quick-view__price, .quick-view__discount-price');
        if (priceElement) {
          const priceText = priceElement.textContent.replace(/[^0-9.]/g, '');
          price = parseFloat(priceText) * 100;
        }
      }
    }

    return price;
  }

  /**
   * Create Afterpay placements for a container
   */
  createAfterpayPlacements(container, price) {
    if (typeof Afterpay === 'undefined' || !price || price <= 0) {
      // If Afterpay is not available, try again in a few seconds
      if (typeof Afterpay === 'undefined' && price > 0) {
        setTimeout(() => {
          this.createAfterpayPlacements(container, price);
        }, 2000);
      }
      return;
    }

    const afterpayContainer = container.querySelector('.installment-payments__afterpay-container');
    if (!afterpayContainer) {
      return;
    }

    // Clear any existing placements first
    const existingPlacements = afterpayContainer.querySelectorAll('afterpay-placement');
    existingPlacements.forEach(placement => placement.remove());

    try {
      Afterpay.createPlacements({
        targetSelector: '.installment-payments__afterpay-container',
        attributes: {
          currency: 'USD',
          locale: 'en_US',
          amount: price / 100,
        },
      });
    } catch (error) {
      console.error('Error creating Afterpay placement:', error);

      // Fallback: create placement element manually
      const placement = document.createElement('afterpay-placement');
      placement.setAttribute('data-currency', 'USD');
      placement.setAttribute('data-locale', 'en_US');
      placement.setAttribute('data-amount', price / 100);
      afterpayContainer.appendChild(placement);
    }

    setTimeout(() => {
      const misplacedPlacements = container.querySelectorAll('afterpay-placement');
      misplacedPlacements.forEach(placement => {
        if (!afterpayContainer.contains(placement)) {
          afterpayContainer.appendChild(placement);
        }
      });

      const placement = afterpayContainer.querySelector('afterpay-placement');
      if (placement) {
        try {
          const shadowRoot = placement.shadowRoot;
          if (shadowRoot) {
            const textElements = shadowRoot.querySelectorAll('.afterpay-main-text');
            textElements.forEach(element => {
              element.style.display = 'none';
              element.style.visibility = 'hidden';
            });
          }
        } catch (error) {
        }

        placement.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openAfterpayModal();
        });
      }
    }, 1000);
  }

  /**
   * Open the custom Afterpay modal
   */
  openAfterpayModal() {
    if (window.KENDO && window.KENDO.store) {
      window.KENDO.store.dispatch('global/openModal', 'afterpay');
    } else if (window.store) {
      window.store.dispatch('global/openModal', 'afterpay');
    }
  }

  /**
   * Update Afterpay placements for a specific component
   */
  updateAfterpayForComponent(componentElement, price) {
    if (!componentElement) return;

    const installmentContainer = componentElement.querySelector('.installment-payments-container');
    if (installmentContainer) {
      installmentContainer.setAttribute('data-price', price);
    }

    const containers = componentElement.querySelectorAll('.installment-payments');
    containers.forEach(container => {
      this.createAfterpayPlacements(container, price);
    });
  }

  /**
   * Update all Afterpay placements on the page
   */
  updateAllAfterpayPlacements() {
    const containers = document.querySelectorAll('.installment-payments');
    containers.forEach(container => {
      const priceData = container.getAttribute('data-price');
      const price = priceData ? parseFloat(priceData) : 0;

      if (price > 0) {
        this.createAfterpayPlacements(container, price);
      }
    });
  }

  /**
   * Update all Klarna placements on the page
   */
  updateAllPlacements() {
    const containers = document.querySelectorAll('.klarna-container');
    containers.forEach(container => {
      const price = this.getPrice(container);
      this.updateContainer(container, price);
      this.showOrTextByDefault(container);
    });
  }

  /**
   * Show "or" text by default when both Afterpay and Klarna are enabled
   */
  showOrTextByDefault(container) {
    const orText = container.parentElement?.querySelector('.klarna-or-text');
    if (orText) {
      const hasAfterpay = container.parentElement?.querySelector('[data-afterpay-modal]');
      const hasKlarnaContainer = container;

      if (hasAfterpay && hasKlarnaContainer) {
        orText.style.display = 'inline';
      }
    }
  }

  /**
   * Update Klarna and Afterpay placements for a specific component
   */
  updateForComponent(componentElement, price) {
    if (!componentElement) return;

    const installmentContainer = componentElement.querySelector('.installment-payments-container');
    if (installmentContainer) {
      installmentContainer.setAttribute('data-price', price);
    }

    const klarnaContainers = componentElement.querySelectorAll('.klarna-container');
    klarnaContainers.forEach(container => {
      this.updateContainer(container, price);
    });

    this.updateAfterpayForComponent(componentElement, price);
  }

  /**
   * Set up mutation observers to watch for data-price changes
   */
  setupMutationObservers() {
    if (!window.MutationObserver) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-price') {
          this.updateAllPlacements();
        }
      });
    });

    const containers = document.querySelectorAll('.installment-payments-container');
    containers.forEach(container => {
      observer.observe(container, { attributes: true, attributeFilter: ['data-price'] });
    });
  }

  /**
   * Register a component for Klarna updates
   */
  registerComponent(componentElement, componentName) {
    if (componentElement) {
      this.containers.set(componentName, componentElement);
    }
  }

  /**
   * Unregister a component
   */
  unregisterComponent(componentName) {
    this.containers.delete(componentName);
  }
}

window.installmentPaymentsManager = new InstallmentPaymentsManager();

window.updateKlarnaPlacements = () => {
  window.installmentPaymentsManager.updateAllPlacements();
};

window.updateAfterpayPlacements = () => {
  window.installmentPaymentsManager.updateAllAfterpayPlacements();
};

export default InstallmentPaymentsManager;

if (typeof module !== 'undefined' && module.exports) {
  module.exports = InstallmentPaymentsManager;
}
